"use client";

import React, { useMemo, useState } from "react";
import { Box, Button, Chip, Icon, Stack, Typography } from "@mui/material";
import {
  CreditScoreOutlined,
  ErrorOutline,
  ExpandMore,
  Inventory2Outlined,
  LocalShippingOutlined,
  PaymentsOutlined,
  StorefrontOutlined,
} from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import Link from "next/link";

import { CartPricingMode, SVariantDto } from "@/__generated__/sdk";
import { useCartItem, useCartPricingMode } from "@/redux/features/cart/hooks";
import ShieldTickBoldIcon from "@/app/_components/Icons/ShieldTickBoldIcon";
import ShoppingBagIcon from "@/app/_components/Icons/ShoppingBagIcon";
import TrashIcon from "@/app/_components/Icons/TrashIcon";
import AddIcon from "@/app/_components/Icons/AddIcon";
import MinusIcon from "@/app/_components/Icons/MinusIcon";
import { useCartVariantActions } from "../Providers/useCartVariantActions";

import ProductVariantAddToCartSection from "./ProductVariantAddToCartSection";
import VariantPrice from "./VariantPrice";
import { SellerOffer, filterSellerOffers, getSellerOffersForVariants } from "../sellerOffers";

const PREVIEW_COUNT = 2;

type OfferTone = "cash" | "credit";

const ICON_COLOR: Record<OfferTone, string> = {
  cash: "#D48136",
  credit: "#2F80ED",
};

type Props = {
  /** All in-stock variants that match the selected attributes (multi-warehouse). */
  variants: SVariantDto[];
  productId: string;
};

function MetaItem({ icon, children }: { icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <Stack direction="row" alignItems="center" gap={0.4} sx={{ color: "grey.600", minWidth: 0 }}>
      <Box sx={{ display: "inline-flex", fontSize: 16, flexShrink: 0 }}>{icon}</Box>
      <Typography variant="caption" sx={{ fontSize: "0.75rem", lineHeight: 1.5 }}>
        {children}
      </Typography>
    </Stack>
  );
}

function CartControls({ offer, productId, fullWidth }: { offer: SellerOffer; productId: string; fullWidth?: boolean }) {
  const cartPricingMode = useCartPricingMode();
  const maxBasketSize = offer.variant.offer?.maxBasketCount ?? offer.variant.maxBasketCount ?? 0;
  const { handleQuantityChange, isPending } = useCartVariantActions(productId, offer.variantId, Number(maxBasketSize) || 0);
  const variantCartItem = useCartItem(offer.variantId);
  const cartItem = variantCartItem && (!cartPricingMode || cartPricingMode === offer.mode) ? variantCartItem : null;
  const quantity = cartItem?.quantity ?? 0;
  const disableAdd = Boolean(cartItem && quantity >= maxBasketSize);

  if (cartItem) {
    return (
      <Stack direction="row" alignItems="center" gap={0.75} sx={{ flexShrink: 0, width: fullWidth ? "100%" : "auto" }}>
        <Button
          component={Link}
          href="/cart"
          variant="contained"
          disableElevation
          size="small"
          startIcon={<ShoppingBagIcon />}
          sx={{
            height: 44,
            flex: fullWidth ? 1 : undefined,
            minWidth: fullWidth ? 0 : 72,
            borderRadius: 2,
            bgcolor: "grey.900",
            fontWeight: 700,
            fontSize: "0.75rem",
            px: 1.25,
          }}
        >
          سبد خرید
        </Button>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.25,
            border: "1px solid",
            borderColor: "grey.200",
            borderRadius: 2,
            p: 0.25,
          }}
        >
          <Button
            variant="contained"
            disableElevation
            disabled={disableAdd || isPending}
            onClick={() => handleQuantityChange(quantity + 1, offer.mode)}
            sx={{ minWidth: 32, width: 32, height: 32, p: 0, borderRadius: 1.5 }}
          >
            <Icon fontSize="small">
              <AddIcon />
            </Icon>
          </Button>
          <Typography variant="body2" fontWeight={800} sx={{ minWidth: 20, textAlign: "center" }}>
            {isPending ? "…" : quantity}
          </Typography>
          <Button
            variant="contained"
            disableElevation
            disabled={isPending}
            onClick={() => handleQuantityChange(quantity - 1, offer.mode)}
            sx={{ minWidth: 32, width: 32, height: 32, p: 0, borderRadius: 1.5 }}
          >
            <Icon fontSize="small">{quantity === 1 ? <TrashIcon /> : <MinusIcon />}</Icon>
          </Button>
        </Box>
      </Stack>
    );
  }

  return (
    <LoadingButton
      loading={isPending}
      onClick={() => handleQuantityChange(1, offer.mode)}
      variant="contained"
      disableElevation
      startIcon={<ShoppingBagIcon />}
      fullWidth={fullWidth}
      sx={{
        flex: fullWidth ? undefined : { xs: "1 1 148px", md: "0 0 auto" },
        height: 44,
        minWidth: fullWidth ? 0 : { xs: 0, sm: 168 },
        px: { xs: 1.25, sm: 2 },
        borderRadius: 2,
        bgcolor: "grey.900",
        fontWeight: 800,
        fontSize: { xs: "0.75rem", sm: "0.8125rem" },
        whiteSpace: "nowrap",
      }}
    >
      افزودن به سبد خرید
    </LoadingButton>
  );
}

type MetaChipTone = "ready" | "ship" | "guarantee";

const META_CHIP_TONE: Record<MetaChipTone, { bg: string; color: string; border: string }> = {
  ready: { bg: "rgba(46, 125, 50, 0.1)", color: "#2E7D32", border: "rgba(46, 125, 50, 0.22)" },
  ship: { bg: "rgba(2, 136, 209, 0.1)", color: "#0277BD", border: "rgba(2, 136, 209, 0.22)" },
  guarantee: { bg: "rgba(212, 129, 54, 0.12)", color: "#B86A1C", border: "rgba(212, 129, 54, 0.28)" },
};

function MetaChip({ tone, icon, children }: { tone: MetaChipTone; icon: React.ReactNode; children: React.ReactNode }) {
  const palette = META_CHIP_TONE[tone];
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.4,
        maxWidth: "100%",
        px: 0.85,
        py: 0.4,
        borderRadius: 1.5,
        bgcolor: palette.bg,
        color: palette.color,
        border: "1px solid",
        borderColor: palette.border,
        fontSize: "0.7rem",
        fontWeight: 700,
        lineHeight: 1.3,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        "& > svg, & > .MuiBox-root": { flexShrink: 0, fontSize: 13, width: 13, height: 13 },
      }}
    >
      {icon}
      {children}
    </Box>
  );
}

function SellerRow({
  offer,
  productId,
  compact,
  rank,
}: {
  offer: SellerOffer;
  productId: string;
  compact?: boolean;
  /** 1-based rank in the sorted sheet list (1 = cheapest). */
  rank?: number;
}) {
  const cartPricingMode = useCartPricingMode();
  const variantCartItem = useCartItem(offer.variantId);
  const isInCart = Boolean(variantCartItem && (!cartPricingMode || cartPricingMode === offer.mode));
  const isCredit = offer.mode === CartPricingMode.Credit;
  const iconColor = isCredit ? ICON_COLOR.credit : ICON_COLOR.cash;
  const isBest = rank === 1;
  const maxBasketSize = offer.variant.offer?.maxBasketCount ?? offer.variant.maxBasketCount ?? 0;
  const { handleQuantityChange, isPending } = useCartVariantActions(productId, offer.variantId, Number(maxBasketSize) || 0);

  if (compact) {
    return (
      <Box
        data-seller-mode={offer.mode}
        data-seller-variant={offer.variantId}
        sx={{
          bgcolor: isBest ? "rgba(46, 125, 50, 0.04)" : "#fff",
          borderRadius: 2.5,
          border: "1px solid",
          borderColor: isInCart ? "grey.900" : isBest ? "success.light" : "grey.200",
          overflow: "hidden",
        }}
      >
        {isBest ? (
          <Box
            sx={{
              px: 1.25,
              py: 0.4,
              bgcolor: "success.main",
              color: "#fff",
              fontSize: "0.68rem",
              fontWeight: 800,
              letterSpacing: "-0.01em",
            }}
          >
            ارزان‌ترین پیشنهاد
          </Box>
        ) : null}

        <Stack spacing={1} sx={{ p: 1.25 }}>
          <Stack direction="row" alignItems="center" gap={0.75} minWidth={0}>
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: 1.5,
                bgcolor: isCredit ? "rgba(47,128,237,0.1)" : "rgba(212,129,54,0.12)",
                color: iconColor,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <StorefrontOutlined sx={{ fontSize: 18 }} />
            </Box>
            <Typography fontWeight={800} noWrap sx={{ flex: 1, minWidth: 0, fontSize: "0.875rem", lineHeight: 1.35 }}>
              {offer.label}
            </Typography>
            {rank != null && rank > 1 ? (
              <Typography variant="caption" color="grey.500" sx={{ flexShrink: 0, fontSize: "0.7rem", fontWeight: 700 }}>
                #{rank.toLocaleString("fa-IR")}
              </Typography>
            ) : null}
          </Stack>

          <Stack direction="row" flexWrap="wrap" useFlexGap gap={0.5}>
            <MetaChip tone="ready" icon={<Inventory2Outlined />}>
              آماده ارسال
            </MetaChip>
            {offer.shippingOriginLabel ? (
              <MetaChip tone="ship" icon={<LocalShippingOutlined />}>
                {offer.shippingOriginLabel}
              </MetaChip>
            ) : null}
            {offer.guaranteeTitle ? (
              <MetaChip
                tone="guarantee"
                icon={
                  <Box sx={{ display: "inline-flex", "& svg": { width: 13, height: 13 } }}>
                    <ShieldTickBoldIcon />
                  </Box>
                }
              >
                {offer.guaranteeTitle}
              </MetaChip>
            ) : null}
          </Stack>

          {offer.stockLeft != null ? (
            <Stack direction="row" alignItems="center" gap={0.4} sx={{ color: "error.main" }}>
              <ErrorOutline sx={{ fontSize: 14 }} />
              <Typography variant="caption" fontWeight={700} sx={{ fontSize: "0.7rem" }}>
                فقط {offer.stockLeft.toLocaleString("fa-IR")} عدد مانده
              </Typography>
            </Stack>
          ) : null}

          <Stack
            spacing={1}
            sx={{
              pt: 1,
              mt: 0.25,
              borderTop: "1px solid",
              borderColor: "grey.100",
            }}
          >
            <VariantPrice variant={offer.variant as SVariantDto} sellerOffer={offer} layout="inline" />
            <ProductVariantAddToCartSection
              variant={offer.variant as SVariantDto}
              productId={productId}
              layout="buybox"
              pricingMode={offer.mode}
              handleQuantityChange={handleQuantityChange}
              isPending={isPending}
            />
          </Stack>
        </Stack>
      </Box>
    );
  }

  return (
    <Box
      data-seller-mode={offer.mode}
      data-seller-variant={offer.variantId}
      sx={{
        bgcolor: "#fff",
        borderRadius: "10px",
        boxShadow: "0 1px 8px rgba(33, 33, 33, 0.06)",
        border: "1px solid",
        borderColor: isInCart ? "grey.900" : "transparent",
        px: { xs: 1.5, md: 2 },
        py: { xs: 1.5, md: 1.75 },
      }}
    >
      <Stack
        direction={{ xs: "column", md: "row" }}
        alignItems={{ xs: "stretch", md: "center" }}
        justifyContent="space-between"
        gap={{ xs: 1.25, md: 2 }}
      >
        <Stack spacing={0.75} flex={1} minWidth={0}>
          <Stack direction="row" alignItems="center" gap={0.75} flexWrap="wrap" useFlexGap>
            <StorefrontOutlined sx={{ fontSize: 18, color: iconColor }} />
            <Typography fontWeight={800} sx={{ fontSize: { xs: "0.9rem", md: "1rem" }, lineHeight: 1.4 }}>
              {offer.label}
            </Typography>
            {isCredit ? (
              <Chip
                size="small"
                label="اعتباری"
                sx={{
                  height: 22,
                  fontWeight: 700,
                  fontSize: "0.7rem",
                  bgcolor: "info.50",
                  color: "info.main",
                  border: "1px solid",
                  borderColor: "info.200",
                }}
              />
            ) : null}
          </Stack>

          <Stack
            direction="row"
            divider={<Box sx={{ display: { xs: "none", md: "block" } }}>.</Box>}
            alignItems="center"
            flexWrap="wrap"
            useFlexGap
            gap={{ xs: 0.75, sm: 1.5 }}
          >
            {offer.shippingOriginLabel ? (
              <MetaItem icon={<LocalShippingOutlined sx={{ fontSize: 16, color: iconColor }} />}>{offer.shippingOriginLabel}</MetaItem>
            ) : null}
            <MetaItem icon={<Inventory2Outlined sx={{ fontSize: 16, color: iconColor }} />}>موجود در انبار فروشنده</MetaItem>
            {offer.guaranteeTitle ? (
              <MetaItem
                icon={
                  <Box sx={{ color: iconColor, display: "inline-flex", "& svg": { width: 16, height: 16 } }}>
                    <ShieldTickBoldIcon />
                  </Box>
                }
              >
                {offer.guaranteeTitle}
              </MetaItem>
            ) : null}
          </Stack>
        </Stack>
        {offer.stockLeft != null ? (
          <Stack direction="row" alignItems="center" gap={0.4} sx={{ color: "error.main" }}>
            <ErrorOutline sx={{ fontSize: 15 }} />
            <Typography variant="caption" fontWeight={700}>
              تنها {offer.stockLeft.toLocaleString("fa-IR")} عدد باقی مانده است
            </Typography>
          </Stack>
        ) : null}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          useFlexGap
          gap={1.25}
          sx={{ width: { xs: "100%", md: "auto" }, flexShrink: 0 }}
        >
          <Box sx={{ minWidth: 0 }}>
            <VariantPrice variant={offer.variant as SVariantDto} sellerOffer={offer} layout="inline" />
          </Box>
          <CartControls offer={offer} productId={productId} />
        </Stack>
      </Stack>
    </Box>
  );
}

/** Flat seller rows for the mobile sheet (no group chrome / expand). */
export function SellerOfferRows({
  offers,
  productId,
  compact,
}: {
  offers: SellerOffer[];
  productId: string;
  compact?: boolean;
}) {
  if (!offers.length) return null;

  return (
    <Stack spacing={compact ? 1 : 1.25} data-seller-offers>
      {offers.map((offer, index) => (
        <SellerRow
          key={offer.key}
          offer={offer}
          productId={productId}
          compact={compact}
          rank={compact ? index + 1 : undefined}
        />
      ))}
    </Stack>
  );
}

function SellerGroup({
  id,
  title,
  offers,
  productId,
  toneKey,
}: {
  id?: string;
  title: string;
  offers: SellerOffer[];
  productId: string;
  toneKey: OfferTone;
}) {
  const [expanded, setExpanded] = useState(false);
  if (!offers.length) return null;

  const visible = expanded ? offers : offers.slice(0, PREVIEW_COUNT);
  const HeaderIcon = toneKey === "credit" ? CreditScoreOutlined : PaymentsOutlined;

  return (
    <Box
      id={id}
      data-price-role={toneKey}
      sx={{
        bgcolor: "#F6F6F7",
        borderRadius: 3,
        px: { xs: 1.25, sm: 2 },
        py: { xs: 1.5, sm: 2 },
        scrollMarginTop: { xs: 96, md: 140 },
      }}
    >
      <Stack direction="row" alignItems="center" gap={0.75} sx={{ mb: 1.5 }}>
        <HeaderIcon sx={{ fontSize: 20, color: ICON_COLOR[toneKey] }} />
        <Typography component="h2" fontWeight={800} sx={{ fontSize: { xs: "1rem", md: "1.125rem" } }}>
          {title}
        </Typography>
      </Stack>
      <SellerOfferRows offers={visible} productId={productId} />
      {offers.length > PREVIEW_COUNT ? (
        <Button
          fullWidth
          onClick={() => setExpanded((value) => !value)}
          endIcon={<ExpandMore sx={{ transform: expanded ? "rotate(180deg)" : "none", transition: "transform 0.2s" }} />}
          sx={{ mt: 1, color: "grey.800", fontWeight: 800 }}
        >
          {expanded ? "نمایش کمتر" : "مشاهده بیشتر"}
        </Button>
      ) : null}
    </Box>
  );
}

/**
 * Full-width seller list under the product hero (desktop).
 * Cash and credit are separate blocks, each showing two rows until expanded.
 */
export default function VariantSellerOffers({ variants, productId }: Props) {
  const allOffers = useMemo(() => getSellerOffersForVariants(variants), [variants]);
  const cashOffers = useMemo(() => filterSellerOffers(allOffers, "cash"), [allOffers]);
  const creditOffers = useMemo(() => filterSellerOffers(allOffers, "credit"), [allOffers]);

  if (allOffers.length < 2) return null;

  const bothModes = cashOffers.length > 0 && creditOffers.length > 0;

  return (
    <Stack id="seller-offers" spacing={2} data-seller-offers sx={{ scrollMarginTop: { xs: 96, md: 140 } }}>
      {bothModes ? (
        <>
          <SellerGroup toneKey="cash" title="فروشندگان خرید نقدی" offers={cashOffers} productId={productId} />
          <SellerGroup id="seller-offers-credit" toneKey="credit" title="فروشندگان طرف‌قرارداد اعتباری" offers={creditOffers} productId={productId} />
        </>
      ) : (
        <SellerGroup
          toneKey={creditOffers.length ? "credit" : "cash"}
          title={creditOffers.length ? "فروشندگان طرف‌قرارداد اعتباری" : "همه فروشندگان"}
          offers={allOffers}
          productId={productId}
        />
      )}
    </Stack>
  );
}
